import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import AdminShell from "@/components/admin/AdminShell";
import RebookModal from "@/components/reiki/RebookModal";
import { CancelRegistrationModal } from "@/components/admin/CancelRegistrationModal";
import { Loader2, Trash2, DollarSign, Ban, CheckCircle2, Plus, CalendarDays, Mail } from "lucide-react";

const DOW_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]; // matches Python datetime.weekday() (Mon=0)
const TIER_ORDER = ["private", "semi-private", "group-3", "group-4-5", "long-distance"];

export default function AdminReikiBookings() {
  const [tab, setTab] = useState("bookings");
  return (
    <AdminShell>
      <div className="max-w-6xl mx-auto py-8">
        <h1 className="font-display text-4xl text-aviana-ink mb-6">Reiki Bookings</h1>
        <div className="flex gap-2 mb-8 border-b border-aviana-ink/10">
          {[
            { id: "bookings", label: "Bookings" },
            { id: "availability", label: "Availability" },
            { id: "templates", label: "Prep Emails" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm border-b-2 transition-colors -mb-px ${
                tab === t.id
                  ? "border-aviana-gold text-aviana-ink"
                  : "border-transparent text-aviana-muted hover:text-aviana-ink"
              }`}
              data-testid={`admin-reiki-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {tab === "bookings" && <BookingsList />}
        {tab === "availability" && <AvailabilityManager />}
        {tab === "templates" && <TemplatesEditor />}
      </div>
    </AdminShell>
  );
}

function BookingsList() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState({});
  const [rebookFor, setRebookFor] = useState(null); // booking object being rebooked
  const [cancelFor, setCancelFor] = useState(null); // booking id being cancelled

  const load = async () => {
    try {
      const { data } = await api.get("/admin/reiki/bookings");
      setRows(data || []);
    } catch (e) {
      toast.error("Could not load bookings");
      setRows([]);
    }
  };
  useEffect(() => { load(); }, []);

  const doAction = async (id, fn) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await fn();
      await load();
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const chargeHold = (b) => doAction(b.id, async () => {
    const amount = parseFloat(window.prompt(
      `Charge held card — how much? (cancellation fee is typically full session cost $${b.total_usd})`,
      String(b.total_usd)
    ) || "");
    if (!amount) return;
    const reason = window.prompt("Reason (cancellation_fee | no_show | session_payment)", "cancellation_fee");
    try {
      await api.post(`/admin/reiki/bookings/${b.id}/charge`, { amount_usd: amount, reason: reason || "cancellation_fee" });
      toast.success(`Charged $${amount}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Charge failed");
    }
  });

  const markPaid = (b) => doAction(b.id, async () => {
    const method = window.prompt("Method (venmo | zelle | cash | comp)", "venmo");
    if (!method) return;
    const amount = parseFloat(window.prompt(`Amount received`, String(b.total_usd - (b.amount_paid_usd || 0))) || "");
    if (!amount) return;
    try {
      await api.post(`/admin/reiki/bookings/${b.id}/mark-paid`, { method, amount_usd: amount });
      toast.success("Recorded");
    } catch (e) {
      toast.error("Failed");
    }
  });

  const setStatus = (b, status) => doAction(b.id, async () => {
    try {
      await api.post(`/admin/reiki/bookings/${b.id}/status`, { status });
      toast.success(`Marked ${status}`);
    } catch (e) {
      toast.error("Failed");
    }
  });

  if (rows === null) return <div className="py-10 text-center text-aviana-muted"><Loader2 className="animate-spin inline mr-2" size={16} />Loading…</div>;
  if (rows.length === 0) return <p className="text-aviana-muted italic">No bookings yet.</p>;

  return (
    <div className="space-y-4" data-testid="admin-reiki-bookings-list">
      {rows.map((b) => {
        const start = new Date(b.start_iso);
        const paidStr = b.amount_paid_usd
          ? `$${b.amount_paid_usd.toFixed(2)} paid`
          : "Not paid";
        return (
          <div key={b.id} className="border border-aviana-ink/10 rounded-xl p-5 bg-white/70">
            <div className="flex flex-wrap justify-between gap-4">
              <div>
                <div className="font-display text-xl text-aviana-ink">{b.tier_name}</div>
                <div className="text-sm text-aviana-muted mt-1">
                  {start.toLocaleString([], {
                    weekday: "long", month: "long", day: "numeric",
                    hour: "numeric", minute: "2-digit",
                  })}
                  {" · "}{b.duration_min} min
                </div>
                <div className="text-sm text-aviana-ink mt-2">
                  {b.lead_name} · {b.user_email} · {b.lead_phone}
                </div>
                <div className="text-xs text-aviana-muted mt-1">
                  {b.attendee_count} attendee(s)
                  {b.attendees?.length > 0 && ` — ${b.attendees.map((a) => a.name).join(", ")}`}
                </div>
              </div>
              <div className="text-right">
                <StatusPill status={b.status} />
                <div className="mt-2 text-sm">
                  <div className="text-aviana-ink">${b.total_usd} total</div>
                  <div className="text-aviana-muted text-xs">{paidStr}</div>
                  {b.hold_payment_method_id && !b.deposit_intent_id && (
                    <div className="text-[10px] uppercase tracking-wider text-aviana-sage mt-1">
                      Card on file · not charged
                    </div>
                  )}
                  {b.deposit_intent_id && (
                    <div className="text-[10px] uppercase tracking-wider text-aviana-plum mt-1">
                      $100 deposit charged
                    </div>
                  )}
                  {b.session_payment_method && (
                    <div className="text-xs mt-1 text-aviana-plum">
                      via {b.session_payment_method}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {(b.intention || b.physical_limitations) && (
              <div className="mt-4 text-xs text-aviana-muted grid md:grid-cols-2 gap-3 bg-aviana-cream/30 rounded-lg p-3">
                {b.intention && <div><strong>Intention:</strong> {b.intention}</div>}
                {b.physical_limitations && <div><strong>Physical notes:</strong> {b.physical_limitations}</div>}
              </div>
            )}
            <div className="mt-4 flex flex-wrap gap-2 text-xs">
              {b.hold_payment_method_id && (
                <button
                  onClick={() => chargeHold(b)}
                  disabled={busy[b.id]}
                  className="px-3 py-1.5 rounded-md border border-aviana-plum/30 text-aviana-plum hover:bg-aviana-plum hover:text-white transition-colors"
                  data-testid={`admin-reiki-charge-${b.id}`}
                >
                  <DollarSign size={12} className="inline -mt-0.5 mr-1" />
                  Charge held card
                </button>
              )}
              {b.hold_payment_method_id && (
                <button
                  onClick={() => setRebookFor(b)}
                  disabled={busy[b.id]}
                  className="px-3 py-1.5 rounded-md border border-aviana-gold/40 text-aviana-goldDeep hover:bg-aviana-gold hover:text-white transition-colors"
                  data-testid={`admin-reiki-rebook-${b.id}`}
                >
                  <CalendarDays size={12} className="inline -mt-0.5 mr-1" />
                  Rebook
                </button>
              )}
              <button
                onClick={() => markPaid(b)}
                disabled={busy[b.id]}
                className="px-3 py-1.5 rounded-md border border-aviana-ink/15 text-aviana-ink hover:border-aviana-gold"
              >
                Mark paid (Venmo/Zelle)
              </button>
              {b.status !== "completed" && (
                <button
                  onClick={() => setStatus(b, "completed")}
                  disabled={busy[b.id]}
                  className="px-3 py-1.5 rounded-md border border-aviana-sage/30 text-aviana-sage hover:bg-aviana-sage hover:text-white transition-colors"
                >
                  <CheckCircle2 size={12} className="inline -mt-0.5 mr-1" />
                  Mark complete
                </button>
              )}
              {b.status !== "cancelled" && (
                <button
                  onClick={() => setCancelFor(b.id)}
                  disabled={busy[b.id]}
                  className="px-3 py-1.5 rounded-md border border-red-200 text-red-600 hover:bg-red-500 hover:text-white transition-colors"
                  data-testid={`admin-reiki-cancel-${b.id}`}
                >
                  <Ban size={12} className="inline -mt-0.5 mr-1" />
                  Cancel
                </button>
              )}
              {b.status !== "no_show" && (
                <button
                  onClick={() => setStatus(b, "no_show")}
                  disabled={busy[b.id]}
                  className="px-3 py-1.5 rounded-md border border-aviana-ink/15 text-aviana-ink"
                >
                  No-show
                </button>
              )}
            </div>
            {b.admin_charges?.length > 0 && (
              <div className="mt-3 text-xs text-aviana-muted">
                <strong>Charges:</strong>{" "}
                {b.admin_charges.map((c, i) => (
                  <span key={i} className="inline-block mr-3">
                    ${c.amount_usd} ({c.reason}) — {c.status}
                  </span>
                ))}
              </div>
            )}
            {b.refunds?.length > 0 && (
              <div className="mt-1 text-xs text-aviana-muted">
                <strong>Refunds:</strong>{" "}
                {b.refunds.map((r, i) => (
                  <span key={i} className="inline-block mr-3">
                    ${r.amount_usd} — {r.status}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {rebookFor && (
        <RebookModal
          source={rebookFor}
          onClose={() => setRebookFor(null)}
          onSaved={async () => { setRebookFor(null); await load(); }}
        />
      )}
      {cancelFor && (
        <CancelRegistrationModal
          source="reiki"
          id={cancelFor}
          onClose={() => setCancelFor(null)}
          onDone={async () => { setCancelFor(null); await load(); }}
        />
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    confirmed: "bg-aviana-sage/20 text-aviana-sage",
    pending_hold: "bg-yellow-100 text-yellow-800",
    pending_deposit: "bg-yellow-100 text-yellow-800",
    cancelled: "bg-red-100 text-red-700",
    completed: "bg-aviana-plum/20 text-aviana-plum",
    no_show: "bg-red-100 text-red-700",
  };
  return (
    <span className={`px-3 py-1 rounded-full text-xs uppercase tracking-wider ${map[status] || "bg-aviana-ink/10 text-aviana-muted"}`}>
      {status?.replace(/_/g, " ")}
    </span>
  );
}


function AvailabilityManager() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ kind: "recurring", dow: 0, start: "10:00", end: "16:00", date: "" });
  const [busy, setBusy] = useState(false);
  const [bulk, setBulk] = useState({ date_from: "", date_to: "", start: "00:00", end: "23:59" });
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/reiki/availability");
      setRows(data || []);
    } catch (e) {
      setRows([]);
    }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    setBusy(true);
    try {
      const payload = { kind: form.kind, start: form.start, end: form.end };
      if (form.kind === "recurring") payload.dow = parseInt(form.dow, 10);
      else payload.date = form.date;
      await api.post("/admin/reiki/availability", payload);
      toast.success("Added");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const bulkBlock = async () => {
    if (!bulk.date_from || !bulk.date_to) {
      toast.error("Pick a start and end date");
      return;
    }
    if (bulk.date_to < bulk.date_from) {
      toast.error("End date must be on or after start date");
      return;
    }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/reiki/availability/bulk-block", bulk);
      toast.success(`Blocked ${data.inserted} day${data.inserted === 1 ? "" : "s"}`);
      setBulk({ date_from: "", date_to: "", start: "00:00", end: "23:59" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this window?")) return;
    await api.delete(`/admin/reiki/availability/${id}`);
    await load();
  };

  if (rows === null) return <div className="py-10 text-center text-aviana-muted"><Loader2 className="animate-spin inline mr-2" size={16} />Loading…</div>;

  const recurring = rows.filter((r) => r.kind === "recurring").sort((a, b) => a.dow - b.dow);
  const opens = rows.filter((r) => r.kind === "open").sort((a, b) => a.date.localeCompare(b.date));
  const blocks = rows.filter((r) => r.kind === "block").sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="grid lg:grid-cols-2 gap-8" data-testid="admin-reiki-availability">
      <div>
        <h3 className="font-display text-2xl text-aviana-ink mb-4">Add a window</h3>
        <div className="space-y-3 border border-aviana-ink/10 rounded-xl p-5 bg-white/70">
          <div>
            <label className="block text-xs uppercase tracking-wider text-aviana-muted mb-1">Kind</label>
            <select
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value })}
              className="w-full border border-aviana-ink/15 rounded-md px-3 py-2 text-sm"
              data-testid="admin-reiki-avail-kind"
            >
              <option value="recurring">Recurring (weekly)</option>
              <option value="open">One-off open (specific date)</option>
              <option value="block">Block (vacation / off day)</option>
            </select>
          </div>
          {form.kind === "recurring" ? (
            <div>
              <label className="block text-xs uppercase tracking-wider text-aviana-muted mb-1">Day of week</label>
              <select
                value={form.dow}
                onChange={(e) => setForm({ ...form, dow: e.target.value })}
                className="w-full border border-aviana-ink/15 rounded-md px-3 py-2 text-sm"
              >
                {DOW_LABELS.map((lbl, i) => (
                  <option key={i} value={i}>{lbl}</option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-xs uppercase tracking-wider text-aviana-muted mb-1">Date</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="w-full border border-aviana-ink/15 rounded-md px-3 py-2 text-sm"
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-aviana-muted mb-1">Start</label>
              <input
                type="time"
                value={form.start}
                onChange={(e) => setForm({ ...form, start: e.target.value })}
                className="w-full border border-aviana-ink/15 rounded-md px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-aviana-muted mb-1">End</label>
              <input
                type="time"
                value={form.end}
                onChange={(e) => setForm({ ...form, end: e.target.value })}
                className="w-full border border-aviana-ink/15 rounded-md px-3 py-2 text-sm"
              />
            </div>
          </div>
          <button
            onClick={add}
            disabled={busy}
            className="btn-primary w-full mt-3"
            data-testid="admin-reiki-avail-add"
          >
            <Plus size={14} className="inline -mt-0.5 mr-1" />
            {busy ? "Saving…" : "Add window"}
          </button>
        </div>

        <h3 className="font-display text-2xl text-aviana-ink mt-8 mb-4">Block a date range</h3>
        <div className="space-y-3 border border-aviana-ink/10 rounded-xl p-5 bg-white/70" data-testid="admin-reiki-bulk-block">
          <p className="text-xs text-aviana-muted -mt-1">
            Vacations, tent-down season, out-of-town weeks — blocks every day in the range in one click. Defaults to a full-day block (00:00 → 23:59).
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-aviana-muted mb-1">From</label>
              <input
                type="date"
                value={bulk.date_from}
                onChange={(e) => setBulk({ ...bulk, date_from: e.target.value })}
                className="w-full border border-aviana-ink/15 rounded-md px-3 py-2 text-sm"
                data-testid="admin-reiki-bulk-from"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-aviana-muted mb-1">To</label>
              <input
                type="date"
                value={bulk.date_to}
                onChange={(e) => setBulk({ ...bulk, date_to: e.target.value })}
                className="w-full border border-aviana-ink/15 rounded-md px-3 py-2 text-sm"
                data-testid="admin-reiki-bulk-to"
              />
            </div>
          </div>
          <details className="text-xs text-aviana-muted">
            <summary className="cursor-pointer hover:text-aviana-ink select-none">Restrict to specific hours (optional)</summary>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div>
                <label className="block text-xs uppercase tracking-wider text-aviana-muted mb-1">Start</label>
                <input
                  type="time"
                  value={bulk.start}
                  onChange={(e) => setBulk({ ...bulk, start: e.target.value })}
                  className="w-full border border-aviana-ink/15 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-aviana-muted mb-1">End</label>
                <input
                  type="time"
                  value={bulk.end}
                  onChange={(e) => setBulk({ ...bulk, end: e.target.value })}
                  className="w-full border border-aviana-ink/15 rounded-md px-3 py-2 text-sm"
                />
              </div>
            </div>
          </details>
          <button
            onClick={bulkBlock}
            disabled={bulkBusy}
            className="btn-primary w-full mt-3"
            data-testid="admin-reiki-bulk-submit"
          >
            <Plus size={14} className="inline -mt-0.5 mr-1" />
            {bulkBusy ? "Blocking…" : "Block this range"}
          </button>
        </div>
      </div>
      <div className="space-y-6">
        <AvailList title="Weekly (recurring)" items={recurring} labelFn={(r) => `${DOW_LABELS[r.dow]} · ${r.start} → ${r.end}`} remove={remove} />
        <AvailList title="One-off opens" items={opens} labelFn={(r) => `${r.date} · ${r.start} → ${r.end}`} remove={remove} />
        <AvailList title="Blocked" items={blocks} labelFn={(r) => `${r.date} · ${r.start} → ${r.end}`} remove={remove} />
      </div>
    </div>
  );
}

function AvailList({ title, items, labelFn, remove }) {
  return (
    <div>
      <h4 className="text-sm uppercase tracking-wider text-aviana-muted mb-2">{title}</h4>
      {items.length === 0 ? (
        <p className="text-sm text-aviana-muted italic">None</p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((r) => (
            <li key={r.id} className="flex items-center justify-between text-sm border border-aviana-ink/10 rounded-md px-3 py-2 bg-white/60">
              <span>{labelFn(r)}</span>
              <button
                onClick={() => remove(r.id)}
                className="text-red-500 hover:text-red-700"
                aria-label="Delete"
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TemplatesEditor() {
  const [rows, setRows] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/reiki/email-templates");
        setRows(data || []);
      } catch (e) {
        setRows([]);
      }
    })();
  }, []);

  const save = async (slug) => {
    setSaving((s) => ({ ...s, [slug]: true }));
    try {
      await api.put(`/admin/reiki/email-templates/${slug}`, { body_html: drafts[slug] });
      toast.success("Saved");
      setRows((rows) => rows.map((r) => r.slug === slug ? { ...r, body_html: drafts[slug], customized: true } : r));
      setDrafts((d) => { const { [slug]: _, ...rest } = d; return rest; });
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving((s) => ({ ...s, [slug]: false }));
    }
  };

  if (rows === null) return <div className="py-10 text-center text-aviana-muted"><Loader2 className="animate-spin inline mr-2" size={16} />Loading…</div>;

  const sorted = [...rows].sort((a, b) => TIER_ORDER.indexOf(a.slug) - TIER_ORDER.indexOf(b.slug));
  return (
    <div className="space-y-6" data-testid="admin-reiki-templates">
      <p className="text-sm text-aviana-muted">
        These prep-instruction snippets are included in the confirmation email sent after booking.
        They accept HTML (basic tags like &lt;p&gt;, &lt;strong&gt;, &lt;em&gt; work well).
      </p>
      {sorted.map((r) => (
        <div key={r.slug} className="border border-aviana-ink/10 rounded-xl p-5 bg-white/70">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-display text-lg text-aviana-ink capitalize">{r.slug.replace(/-/g, " ")}</div>
              <div className="text-xs text-aviana-muted">{r.customized ? "Customized" : "Using default"}</div>
            </div>
          </div>
          <textarea
            defaultValue={r.body_html}
            onChange={(e) => setDrafts((d) => ({ ...d, [r.slug]: e.target.value }))}
            className="w-full min-h-[140px] border border-aviana-ink/15 rounded-md px-3 py-2 text-sm font-mono"
            data-testid={`admin-reiki-tpl-${r.slug}`}
          />
          <button
            onClick={() => save(r.slug)}
            disabled={saving[r.slug] || drafts[r.slug] === undefined}
            className="btn-primary mt-3 text-sm"
          >
            {saving[r.slug] ? "Saving…" : "Save"}
          </button>
        </div>
      ))}
    </div>
  );
}
